import type { ReactNode } from "react";
import { Link } from "react-router";

export function Chevron() {
  return (
    <svg className="pn-chev" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

/**
 * One row of a grouped list, the phone's settings-style list: an optional icon, a title with a line under
 * it, a value on the right and a chevron when it goes somewhere. A row with `to` is a link; with `onClick`
 * it is a button; with neither it only shows.
 */
export function PnRow({
  to,
  onClick,
  icon,
  iconTone,
  title,
  sub,
  value,
  chevron,
  danger = false,
  wrap = false,
  className,
}: {
  to?: string;
  onClick?: () => void;
  icon?: ReactNode;
  /** The icon tile's colour: teal by default. */
  iconTone?: "plain" | "violet" | "good" | "warn" | "danger";
  title: ReactNode;
  sub?: ReactNode;
  value?: ReactNode;
  /** Shown by default when the row goes somewhere. */
  chevron?: boolean;
  danger?: boolean;
  /** Lets the title run to two lines and the line under it wrap. */
  wrap?: boolean;
  className?: string;
}) {
  const showChev = chevron ?? !!to;
  const cls = ["pn-row", danger ? "danger" : "", wrap ? "wrap" : "", !to && !onClick ? "static" : "", className ?? ""].filter(Boolean).join(" ");
  const inner = (
    <>
      {icon ? <span className={iconTone ? `pn-ico ${iconTone}` : "pn-ico"}>{icon}</span> : null}
      <span className="m">
        <span className="t">{title}</span>
        {sub ? <span className="s">{sub}</span> : null}
      </span>
      {value !== undefined && value !== null ? <span className="v">{value}</span> : null}
      {showChev ? <Chevron /> : null}
    </>
  );
  if (to) {
    return (
      <Link className={cls} to={to} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

/**
 * The bar that slides up from the bottom once something on the screen has changed: Discard and Save, in
 * reach of the thumb. Hidden while nothing changed.
 */
export function SaveBar({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  label = "Save",
}: {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  label?: string;
}) {
  return (
    <div className={dirty || saving ? "pn-savebar on" : "pn-savebar"} aria-hidden={!dirty && !saving}>
      <span className="pn-savebar-t">Unsaved changes</span>
      {onDiscard ? (
        <button type="button" className="pn-btn ghost" onClick={onDiscard} disabled={saving} tabIndex={dirty ? 0 : -1}>
          Discard
        </button>
      ) : null}
      <button type="button" className="pn-btn pri" onClick={onSave} disabled={saving} tabIndex={dirty ? 0 : -1}>
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

/** A segmented control: two to four choices side by side, the phone's way of switching a view. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="pn-seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? "on" : undefined} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
